import {Component, DestroyRef, OnDestroy, ViewChild, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoModule} from '@jsverse/transloco';
import {CodeEntryComponent} from '../../../../../shared/ui/code-entry/code-entry.component';
import {MfaMethod, MfaService, usableMethods} from './mfa.service';
import {StepUpContext, readStepUpContext, resumeUrl, siblingUrl, signInUrl} from './step-up-context';

/** Client-side cooldown between resends — cosmetic; the backend enforces its own. */
const RESEND_COOLDOWN_SEC = 60;
export const MFA_CODE_LENGTH = 6;

type ChallengeState = 'loading' | 'challenge' | 'redirecting' | 'blocked' | 'error';

/**
 * MfaChallengeComponent — the platform-default second-factor page for end
 * users (Story 47.2, AC6). Reached ONLY post-password: `/authorize` 302s here
 * when an MFA-required application is hit by a session that has not done MFA
 * (OAuth mode), or the device page hops here when `/device/approve` answers
 * `mfa_required` (device mode). The 6-digit code is entered in the SAME
 * code-entry component manual device-code entry uses (paste fills all six);
 * a recovery code is the escape hatch. On success the session is elevated
 * server-side and the page resumes where the step-up interrupted.
 *
 * 47.4 publishes operator-customized pages over this exact URL; the SDK's
 * mfa* methods mirror what this component does.
 */
@Component({
  selector: 'app-mfa-challenge',
  standalone: true,
  imports: [
    FormsModule,
    MatCard, MatCardContent,
    MatButton,
    MatFormField, MatLabel, MatInput,
    MatProgressSpinnerModule,
    TranslocoModule,
    CodeEntryComponent,
  ],
  templateUrl: './mfa-challenge.component.html',
  styleUrl: './mfa-page.css',
})
export class MfaChallengeComponent implements OnDestroy {
  state: ChallengeState = 'loading';
  /** The method the code entry is currently for. */
  method: MfaMethod | '' = '';
  /** Every method the user holds (Story 47.5); more than one = the user may pick. */
  methods: MfaMethod[] = [];
  code = '';
  recoveryCode = '';
  useRecovery = false;
  submitting = false;
  resending = false;
  error: string | null = null;
  notice: string | null = null;
  resendIn = 0;
  readonly codeLength = MFA_CODE_LENGTH;

  @ViewChild(CodeEntryComponent) private entry?: CodeEntryComponent;

  private ctx!: StepUpContext;
  private resendTimer: ReturnType<typeof setInterval> | null = null;

  private readonly service = inject(MfaService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe(params => {
      this.ctx = readStepUpContext(params);
      if (!this.ctx.clientId) {
        // No client → nothing to step up; sign in again (return-aware). Story
        // 53.9: a missing SESSION is no longer visible here (it is an HttpOnly
        // cookie) — /mfa/state answers 401 and the error path signs in.
        this.goSignIn();
        return;
      }
      this.loadState();
    });
  }

  ngOnDestroy(): void {
    this.stopCooldown();
  }

  get isEmail(): boolean {
    return this.method === 'email';
  }

  /** Story 47.5: a user holding both methods may switch between them. */
  get canSwitchMethod(): boolean {
    return this.methods.length > 1;
  }

  get otherMethod(): MfaMethod | '' {
    const next: MfaMethod = this.method === 'totp' ? 'email' : 'totp';
    return this.methods.includes(next) ? next : '';
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    return this.useRecovery ? this.recoveryCode.trim().length >= 10 : this.code.length === MFA_CODE_LENGTH;
  }

  private loadState(): void {
    this.state = 'loading';
    this.service.state(this.ctx)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: s => {
          // Resume on SATISFIED (Story 47.6: elevated by a method THIS application
          // accepts) — older servers send only `elevated`, which is the same thing
          // when nothing is restricted.
          if (s.satisfied ?? s.elevated) {
            this.resume();
            return;
          }
          if (!s.enrolled) {
            // Unenrolled user on an MFA-required app → the enrollment interstitial.
            this.state = 'redirecting';
            this.navigate(siblingUrl(this.ctx, '/mfa/enroll'));
            return;
          }
          // Story 47.6: offer only methods ∩ allowed. Nothing usable means the
          // user holds only methods this application rejects — the enrollment
          // page has them prove one and add an accepted one (prove-then-add).
          this.methods = usableMethods(s);
          if (this.methods.length === 0) {
            this.state = 'redirecting';
            this.navigate(siblingUrl(this.ctx, '/mfa/enroll'));
            return;
          }
          // Default to the app when held: nothing is dispatched until the user
          // asks for an email code, and it is the stronger factor (47.1 AC7).
          this.method = this.methods.includes('totp') ? 'totp' : (this.methods[0] ?? '');
          this.state = 'challenge';
          if (this.isEmail) {
            this.sendCode(true);
          }
        },
        error: (err: HttpErrorResponse) => this.applyError(err),
      });
  }

  onCodeChange(value: string): void {
    this.code = value;
    this.error = null;
  }

  /** Six digits entered → submit straight away (the button stays as fallback). */
  onCodeCompleted(value: string): void {
    this.code = value;
    if (!this.useRecovery) {
      this.submit();
    }
  }

  /** Switch the code entry to the other held method (Story 47.5). */
  switchMethod(): void {
    const next = this.otherMethod;
    if (!next || this.submitting) return;
    this.method = next;
    this.useRecovery = false;
    this.error = null;
    this.notice = null;
    this.entry?.reset();
    this.code = '';
    if (next === 'email') {
      // The backend's resend limits hold; a repeat within the interval answers
      // code_sent:false and the "sent recently" notice says so honestly.
      this.sendCode(true);
    }
  }

  toggleRecovery(): void {
    this.useRecovery = !this.useRecovery;
    this.error = null;
    // The @if destroys and recreates the code-entry component, so the cells
    // render empty — clear the model too (47.1's lesson: otherwise Verify stays
    // enabled over a blank field and burns a lockout attempt on a stale code).
    this.code = '';
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.submit();
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.submitting = true;
    this.error = null;
    const factor = this.useRecovery ? {recoveryCode: this.recoveryCode} : {code: this.code};
    this.service.verify(this.ctx, factor)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting = false;
          this.resume();
        },
        error: (err: HttpErrorResponse) => {
          this.submitting = false;
          // Reset FIRST: reset() emits valueChange → onCodeChange clears the
          // error, so the message must land after.
          this.entry?.reset();
          this.code = '';
          this.applyError(err);
        },
      });
  }

  /** Email method: ask the backend for a fresh code (rate-limited server-side). */
  resend(): void {
    this.sendCode(false);
  }

  private sendCode(initial: boolean): void {
    if (this.resending || (!initial && (this.resendIn > 0 || this.submitting))) return;
    this.resending = true;
    this.service.sendCode(this.ctx)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.resending = false;
          this.notice = resp.code_sent ? 'mfa.challenge.codeSent' : 'mfa.challenge.codeSentRecently';
          this.startCooldown();
        },
        error: (err: HttpErrorResponse) => {
          this.resending = false;
          // Cooldown even on failure — no unthrottled retry storm.
          this.startCooldown();
          this.applyError(err);
        },
      });
  }

  backToSignIn(): void {
    this.goSignIn();
  }

  private resume(): void {
    this.state = 'redirecting';
    this.navigate(resumeUrl(this.ctx));
  }

  /** The factor vanished mid-flow (an operator reset) — the user must enrol. */
  private goEnroll(): void {
    this.navigate(siblingUrl(this.ctx, '/mfa/enroll'));
  }

  private goSignIn(): void {
    this.state = 'redirecting';
    this.navigate(signInUrl(this.ctx));
  }

  private applyError(err: HttpErrorResponse): void {
    const envelope = (err.error ?? {}) as {error?: string};
    if (err.status === 401) {
      // Session expired mid-flow → sign in again rather than dead-ending.
      this.goSignIn();
      return;
    }
    if (err.status === 403) {
      // 403 covers two very different things and they must not read the same.
      // account_blocked is the lockout counter tripping — a support case. Any
      // other 403 (a stale or removed client binding) is not a lockout, and
      // telling the user their account is locked is both false and
      // unactionable. (Code review 2026-09-05.)
      // NOTE: the backend answers access_denied for BOTH the lockout and a
      // stale/removed client binding, and after the 2026-09-05 oracle collapse
      // an unusable client answers access_denied too — so the wire gives us
      // nothing to split on. Keeping 'blocked' here is the safer of two
      // imperfect messages: a genuinely locked-out user gets the right advice,
      // and a stale-binding user is told to contact support rather than being
      // silently bounced. Splitting them needs a distinct error code from the
      // server, which is logged in deferred-work.md rather than invented here.
      this.state = 'blocked';
      return;
    }
    if (err.status === 409) {
      // The factor went away underneath us — typically an operator MFA reset
      // landing mid-flow. The user must ENROLL, not "check your code".
      this.goEnroll();
      return;
    }
    switch (envelope.error) {
      case 'invalid_grant':
        this.error = this.useRecovery ? 'mfa.challenge.wrongRecovery' : 'mfa.challenge.wrongCode';
        return;
      case 'invalid_request':
        this.error = 'mfa.challenge.checkCode';
        return;
      default:
        if (this.state === 'loading') {
          this.state = 'error';
        } else {
          this.error = 'mfa.challenge.genericError';
        }
    }
  }

  private startCooldown(): void {
    this.stopCooldown();
    this.resendIn = RESEND_COOLDOWN_SEC;
    this.resendTimer = setInterval(() => {
      this.resendIn = Math.max(0, this.resendIn - 1);
      if (this.resendIn === 0) this.stopCooldown();
    }, 1000);
  }

  private stopCooldown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  // Extracted for testability — specs spy on this to assert the navigation
  // without leaving the karma page (the device page's redirectToLogin pattern).
  protected navigate(url: string): void {
    window.location.href = url;
  }
}
